import React, { useCallback, useRef, useState } from 'react'
import { LoginPresenter } from './login-presenter'
import { signInSchema as loginSchema } from '../../lib/utils/schema'
import { Card, H2, Flex } from '../../lib/components'
import { userServices } from '../../services'
import ReCAPTCHA from 'react-google-recaptcha'
import { useDispatch } from 'react-redux'
import { createConfiguration } from '../../global-state/investor-slice'
import { showBanner } from '../../global-state/banner-slice'
import { useNavigate } from 'react-router-dom'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM, EVENTS } from '../../lib/utils/constants'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { getInvestorDataFromSS } from '../../lib/utils/local-storage'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { Role, Verification } from '../../lib/types'
import { LoginEmailVerificationPanel, OTP_LENGTH } from '../login-v2/login-email-verification-panel'
import {
  completeEmailVerificationLogin,
  getVerifyEmailError,
  isEmailVerificationLoginData,
} from '../../lib/utils/post-verification-auth'

const initialValues = {
  email: '',
  password: '',
  rememberMe: '',
}

const LoginContainer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const reRef = useRef<ReCAPTCHA>(null)
  const { breakpoint } = useBreakpoint()
  const { authToken, investorData } = getInvestorDataFromSS()
  const [pendingVerification, setPendingVerification] = useState<{
    email: string
    userId?: number
  } | null>(null)
  const [otpDigits, setOtpDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''))
  const [isResendingVerification, setIsResendingVerification] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([])

  const focusOtpInput = (index: number) => {
    otpInputRefs.current[index]?.focus()
  }

  const handleOtpChange = (index: number, value: string) => {
    const parsed = value.replace(/\D/g, '').slice(-1)
    const next = [...otpDigits]
    next[index] = parsed
    setOtpDigits(next)
    if (parsed && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1)
    }
  }

  const handleOtpKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      focusOtpInput(index - 1)
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      focusOtpInput(index - 1)
    }
    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1)
    }
  }

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const next = Array(OTP_LENGTH)
      .fill('')
      .map((_, i) => pasted[i] || '')
    setOtpDigits(next)
    focusOtpInput(Math.min(pasted.length, OTP_LENGTH - 1))
  }

  const handleVerifyEmailOtp = useCallback(async () => {
    const userId = pendingVerification?.userId
    if (userId === null) return
    const otp = otpDigits.join('')
    if (otp.length !== OTP_LENGTH) {
      dispatch(showBanner({ text: 'Please enter the 6-digit code.', variant: 'error-banner' }))
      return
    }
    setIsVerifyingOtp(true)
    const response = await userServices.verifyEmail_v1(
      otp,
      String(userId),
      Verification.verifyEmail,
    )
    setIsVerifyingOtp(false)

    if (response?.status === 200 && isEmailVerificationLoginData(response.data)) {
      captureEvent(EVENTS.email_verified, { user: userId })
      dispatch(
        showBanner({
          text: 'Email verified successfully.',
          variant: 'success-banner',
        }),
      )
      completeEmailVerificationLogin(dispatch, response.data)
      setPendingVerification(null)
      setOtpDigits(Array(OTP_LENGTH).fill(''))
      return
    }

    dispatch(
      showBanner({
        text:
          getVerifyEmailError(response?.data)?.message ||
          'Invalid or expired code. Try again or resend the email.',
        variant: 'error-banner',
      }),
    )
  }, [pendingVerification?.userId, otpDigits, dispatch, navigate])

  const handleResendVerification = useCallback(async () => {
    const email = pendingVerification?.email
    if (!email) return
    setIsResendingVerification(true)
    try {
      const response = await userServices.sendVerificationLink_v1(email, Verification.verifyEmail)
      if (!!response && response.status === 200) {
        captureEvent(EVENTS.email_verification_link_sent, { email })
        dispatch(
          showBanner({
            text: 'Verification email sent. Please check your inbox or spam folder.',
            variant: 'success-banner',
          }),
        )
        const uid = (response.data as { userId?: number } | undefined)?.userId
        if (typeof uid === 'number') {
          setPendingVerification((prev) =>
            prev ? { ...prev, userId: uid } : { email, userId: uid },
          )
        }
        setOtpDigits(Array(OTP_LENGTH).fill(''))
        window.setTimeout(() => focusOtpInput(0), 0)
      } else {
        dispatch(
          showBanner({
            text:
              (response?.data && response.data.message) ||
              'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
      }
    } finally {
      setIsResendingVerification(false)
    }
  }, [pendingVerification?.email, dispatch])

  const handleSubmit_v1 = async (val: any) => {
    const token = await reRef.current?.executeAsync()
    reRef.current?.reset()
    setPendingVerification(null)
    setOtpDigits(Array(OTP_LENGTH).fill(''))
    if (!authToken && !investorData) {
      const user = await userServices.userLogin_v1(
        {
          email: val.email?.toLowerCase(),
          password: val.password,
          role: Role.investor,
        },
        { 'g-recaptcha-response': token ? token : '' },
      )
      if (!!user && user.status === 200 && user?.data?.role === Role.investor) {
        dispatch(
          createConfiguration({
            isEmailVerified: user.data.is_verified,
            accountStatus: user.data.account_status,
            ...user.data,
            rememberMe: val.rememberMe ? true : false,
          }),
        )

        if (user.data.is_verified) {
          dispatch(
            showBanner({
              text: 'Logged in successfully',
              variant: 'success-banner',
              duration: 1000,
            }),
          )
        }
        captureEvent(EVENTS.user_logged_in, {
          email: val.email,
        })

        logCrmEvent(
          EngagementEvents.USER_LOGGED_IN_EMAIL,
          { Authorization: user.data.authToken },
          { email: user.data.email },
        )

        navigate('/')
      } else if (!!user && user.status === 200 && user?.data?.role !== Role.investor) {
        dispatch(showBanner({ text: 'Not allowed to login!', variant: 'error-banner' }))
      } else if (!!user && user.status === 401 && user.data.code === 'wrong-user-source') {
        dispatch(showBanner({ text: 'Try login using Google', variant: 'error-banner' }))
      } else if (!!user && user.status === 401 && user.data?.code === 'email-not-verified') {
        const emailLower = (val.email ?? '').toLowerCase()
        const uid = (user.data as { userId?: number }).userId
        setPendingVerification({
          email: emailLower,
          ...(typeof uid === 'number' ? { userId: uid } : {}),
        })
        setOtpDigits(Array(OTP_LENGTH).fill(''))
      } else {
        if (user.data.code === 'account-blocked') {
          dispatch(
            showBanner({
              text: (
                <div data-testid='login-blocked-user-warning'>
                  We have temporarily blocked your profile for security reasons. If this was not
                  intended, please contact us at &nbsp;
                  <a className='basic-anchor' href='mailto:support@credibila.com'>
                    support@credibila.com
                  </a>
                  &nbsp; for assistance.
                </div>
              ),
              variant: 'error-banner',
              duration: 15000,
            }),
          )
        } else {
          dispatch(
            showBanner({
              text: (user.data && user.data.message) || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      }
    } else {
      dispatch(
        showBanner({
          text: 'You already logged in another tab. Please logout and use another account.',
          variant: 'error-banner',
          duration: 3000,
        }),
      )
    }
  }

  const googleLoginHandler = async (res: any, rememberMe: any) => {
    const { access_token } = res
    if (!authToken && !investorData) {
      const user = await userServices.googleSignIn_v1({
        accessToken: access_token,
        role: Role.investor,
        createIfMissing: false,
      })
      if (!!user && user.status === 200 && user?.data?.role === 'INVESTOR') {
        dispatch(
          createConfiguration({
            ...user.data,
            accountStatus: user.data.account_status,
            rememberMe: rememberMe,
          }),
        )

        if (user.data.isEmailVerified) {
          dispatch(
            showBanner({
              text: 'Logged in successfully',
              variant: 'success-banner',
              duration: 1000,
            }),
          )
        }

        logCrmEvent(
          EngagementEvents.USER_LOGGED_IN_SOCIAL,
          { Authorization: user.data.authToken },
          { email: user.data.email, socialPlatform: 'google' },
        )

        navigate('/')
      } else if (!!user && user.status === 200 && user?.data?.role !== 'INVESTOR') {
        dispatch(showBanner({ text: 'Not allowed to login!', variant: 'error-banner' }))
      } else if (!!user && user.status === 401 && user.data?.code === 'no-google-account') {
        dispatch(
          showBanner({
            text: 'No account found. Please sign up with Google first.',
            variant: 'error-banner',
          }),
        )
      } else {
        dispatch(
          showBanner({
            text: (user.data && user.data.message) || 'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
      }
    } else {
      dispatch(
        showBanner({
          text: 'You already logged in another tab. Please logout and use another account.',
          variant: 'error-banner',
        }),
      )
      window.location.reload()
    }
  }

  return (
    <Flex variant='column-center'>
      <H2 text={'Log In'} classes={['mb-48']} />
      <Card classes={breakpoint && breakpoint <= SM ? ['p-24'] : ['p-40']}>
        <LoginPresenter
          initialValues={initialValues}
          validationSchema={loginSchema}
          handleFormSubmit={handleSubmit_v1}
          reRef={reRef}
          googleLoginHandler={googleLoginHandler}
        />
        {pendingVerification && (
          <LoginEmailVerificationPanel
            pending={pendingVerification}
            otpDigits={otpDigits}
            onOtpChange={handleOtpChange}
            onOtpKeyDown={handleOtpKeyDown}
            onOtpPaste={handleOtpPaste}
            onVerify={handleVerifyEmailOtp}
            onResend={handleResendVerification}
            isResending={isResendingVerification}
            isVerifying={isVerifyingOtp}
            setOtpInputRef={(index, el) => {
              otpInputRefs.current[index] = el
            }}
          />
        )}
      </Card>
    </Flex>
  )
}

export { LoginContainer }
